/**
 * CelebrationSettingsPage — HR/Admin tool to configure auto + manual greeting cards
 * for staff birthdays, wedding anniversaries, and work anniversaries.
 *
 * Route: /celebrations-settings
 */
import { useEffect, useMemo, useState } from 'react';
import { v37API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Cake, Heart, Briefcase, PartyPopper, Sparkles, Send, Eye, Save, Clock, Palette, Mail } from 'lucide-react';

const TIMEZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
  'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles', 'UTC',
];

const TEMPLATES = [
  { id: 'modern',   label: 'Modern',   desc: 'Gradient header with bold accent. Best for healthcare brands.' },
  { id: 'minimal',  label: 'Minimal',  desc: 'Clean single-color header. Subtle and professional.' },
  { id: 'festive',  label: 'Festive',  desc: 'Warm orange-to-pink gradient. Birthday parties vibe.' },
  { id: 'classic',  label: 'Classic',  desc: 'Teal-to-accent gradient. Tasteful corporate.' },
];

const PLACEHOLDER_HINTS = '{name}, {hospital_name}, {age}, {years}';

const CelebrationSettingsPage = () => {
  const { user, hospital, hasPermission } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [today, setToday] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewKind, setPreviewKind] = useState('birthday');
  const [previewLoading, setPreviewLoading] = useState(false);

  const isHrOrAdmin = useMemo(() => {
    if (user?.is_super_admin) return true;
    const code = (user?.role_code || '').toLowerCase();
    return ['hospital_admin', 'hr', 'it_admin'].includes(code);
  }, [user]);

  useEffect(() => {
    if (!isHrOrAdmin) { setLoading(false); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHrOrAdmin]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        v37API.getCelebrationSettings(),
        v37API.listCelebrationsToday().catch(() => ({ data: { birthdays: [], wedding_anniversaries: [], work_anniversaries: [] } })),
      ]);
      setSettings(s.data);
      setToday(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const up = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      delete payload.hospital_id; delete payload.last_run_at; delete payload.last_run_summary;
      delete payload.last_run_date; delete payload.updated_at; delete payload.updated_by;
      const res = await v37API.updateCelebrationSettings(payload);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!window.confirm('Send today\'s greetings to everyone with a celebration today?')) return;
    setSending(true);
    try {
      const res = await v37API.sendCelebrationsNow();
      const r = res.data;
      toast.success(`Greetings sent: ${r.sent} ✓ · ${r.failed} failed · ${r.skipped_disabled} skipped`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const refreshPreview = async (kind = previewKind) => {
    if (!settings) return;
    setPreviewLoading(true);
    try {
      const overrides = {
        card_template: settings.card_template,
        card_accent_color: settings.card_accent_color,
        include_logo: settings.include_logo,
        sender_signature: settings.sender_signature,
        footer_text: settings.footer_text,
        birthday_subject: settings.birthday_subject,
        birthday_message: settings.birthday_message,
        wedding_subject: settings.wedding_subject,
        wedding_message: settings.wedding_message,
        work_anniversary_subject: settings.work_anniversary_subject,
        work_anniversary_message: settings.work_anniversary_message,
      };
      const res = await v37API.previewCelebrationCard({
        kind,
        name: 'Alex Singh',
        age: kind === 'birthday' ? 30 : undefined,
        years: kind !== 'birthday' ? 5 : undefined,
        overrides,
      });
      setPreviewHtml(res.data.html);
      setPreviewKind(kind);
    } catch (e) {
      toast.error('Preview failed');
    } finally { setPreviewLoading(false); }
  };

  if (!isHrOrAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="celebrations-restricted">
        <Card className="max-w-md w-full"><CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
          <CardContent>Only HR and Admin can configure celebration greetings.</CardContent></Card>
      </div>
    );
  }

  if (loading || !settings) {
    return <div className="p-8 text-slate-400 text-sm">Loading celebration settings…</div>;
  }

  const totalToday = (today?.birthdays?.length || 0) + (today?.wedding_anniversaries?.length || 0) + (today?.work_anniversaries?.length || 0);

  return (
    <div className="space-y-5" data-testid="celebrations-settings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-600 flex items-center justify-center text-white shadow-md">
            <PartyPopper size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">Celebrations <Sparkles size={16} className="text-fuchsia-500" /></h1>
            <p className="text-sm text-slate-500">Automate or manually send birthday, wedding and work-anniversary greetings to your staff.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refreshPreview()} data-testid="celeb-preview-btn"><Eye size={14} className="mr-1.5" /> Refresh Preview</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="celeb-save-btn"><Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Settings'}</Button>
          <Button onClick={sendNow} disabled={sending || totalToday === 0} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="celeb-send-now-btn">
            <Send size={14} className="mr-1.5" /> {sending ? 'Sending…' : `Send Now (${totalToday})`}
          </Button>
        </div>
      </div>

      {/* Auto schedule + today summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-pink-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Clock size={16} className="text-pink-600" /> Automatic Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">Auto-send daily greetings</p>
                <p className="text-xs text-slate-500">When ON, the system fires off greetings at your chosen time every day until you turn it off.</p>
              </div>
              <Switch checked={!!settings.auto_send_enabled} onCheckedChange={(v) => up('auto_send_enabled', v)} data-testid="celeb-auto-toggle" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Hour (24-hr)</Label>
                <Input type="number" min={0} max={23} value={settings.send_hour ?? 9} onChange={e => up('send_hour', Number(e.target.value))} data-testid="celeb-hour" /></div>
              <div><Label>Minute</Label>
                <Input type="number" min={0} max={59} value={settings.send_minute ?? 0} onChange={e => up('send_minute', Number(e.target.value))} data-testid="celeb-minute" /></div>
              <div><Label>Timezone</Label>
                <Select value={settings.timezone || 'Asia/Kolkata'} onValueChange={v => up('timezone', v)}>
                  <SelectTrigger data-testid="celeb-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ChannelToggle label="Birthdays" icon={Cake} on={settings.send_birthdays} setOn={(v) => up('send_birthdays', v)} testid="celeb-ch-birthday" />
              <ChannelToggle label="Wedding Anniversary" icon={Heart} on={settings.send_wedding_anniversaries} setOn={(v) => up('send_wedding_anniversaries', v)} testid="celeb-ch-wedding" />
              <ChannelToggle label="Work Anniversary" icon={Briefcase} on={settings.send_work_anniversaries} setOn={(v) => up('send_work_anniversaries', v)} testid="celeb-ch-work" />
            </div>
            {settings.last_run_at && (
              <div className="text-xs text-slate-500 bg-emerald-50 border border-emerald-100 rounded-lg p-2">
                Last run: {new Date(settings.last_run_at).toLocaleString('en-IN')} · {settings.last_run_summary?.sent ?? 0} sent · {settings.last_run_summary?.failed ?? 0} failed
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-fuchsia-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><PartyPopper size={16} className="text-fuchsia-600" /> Today's Celebrants</CardTitle>
          </CardHeader>
          <CardContent>
            {totalToday === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No celebrations today.</p>
            ) : (
              <div className="space-y-3 text-sm">
                <CelebrantGroup icon={Cake} color="pink" label="Birthdays" items={today.birthdays.map(p => `${p.name}${p.age ? ` · Turning ${p.age}` : ''}`)} />
                <CelebrantGroup icon={Heart} color="rose" label="Wedding Anniversaries" items={today.wedding_anniversaries.map(p => `${p.name}${p.years ? ` · ${p.years} yrs` : ''}`)} />
                <CelebrantGroup icon={Briefcase} color="blue" label="Work Anniversaries" items={today.work_anniversaries.map(p => `${p.name}${p.years ? ` · ${p.years} yrs` : ''}`)} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Customization + preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Palette size={16} className="text-pink-600" /> Card Customization</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 space-y-4">
              <div>
                <Label>Template Style</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {TEMPLATES.map(t => (
                    <button key={t.id} type="button"
                      onClick={() => up('card_template', t.id)}
                      className={`text-left rounded-lg border p-2.5 transition ${settings.card_template === t.id ? 'border-fuchsia-500 ring-2 ring-fuchsia-200 bg-fuchsia-50' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid={`celeb-tmpl-${t.id}`}>
                      <p className="text-sm font-bold text-slate-800">{t.label}</p>
                      <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>Accent Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" className="h-10 w-10 rounded border border-slate-200 p-0" value={settings.card_accent_color || '#ec4899'} onChange={e => up('card_accent_color', e.target.value)} data-testid="celeb-accent" />
                    <Input value={settings.card_accent_color || ''} onChange={e => up('card_accent_color', e.target.value)} className="flex-1 font-mono text-xs" /></div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label>Include Logo</Label>
                    <p className="text-xs text-slate-500">Renders {hospital?.name || 'hospital'} logo in header</p>
                  </div>
                  <Switch checked={!!settings.include_logo} onCheckedChange={v => up('include_logo', v)} data-testid="celeb-logo-toggle" />
                </div>
                <div><Label>Sender Signature</Label>
                  <Input placeholder="— The HR Team" value={settings.sender_signature || ''} onChange={e => up('sender_signature', e.target.value)} data-testid="celeb-signature" /></div>
              </div>
              <div><Label>Footer Text</Label>
                <Input placeholder={`With love from ${hospital?.name || 'your hospital'} family`} value={settings.footer_text || ''} onChange={e => up('footer_text', e.target.value)} data-testid="celeb-footer" /></div>

              <Tabs value={previewKind} onValueChange={(v) => { setPreviewKind(v); refreshPreview(v); }}>
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="birthday" data-testid="celeb-tab-birthday"><Cake size={14} className="mr-1.5" /> Birthday</TabsTrigger>
                  <TabsTrigger value="wedding" data-testid="celeb-tab-wedding"><Heart size={14} className="mr-1.5" /> Wedding</TabsTrigger>
                  <TabsTrigger value="work" data-testid="celeb-tab-work"><Briefcase size={14} className="mr-1.5" /> Work</TabsTrigger>
                </TabsList>
                <TabsContent value="birthday">
                  <MessageEditor
                    subjectKey="birthday_subject" subjectValue={settings.birthday_subject}
                    messageKey="birthday_message" messageValue={settings.birthday_message}
                    up={up} testidPrefix="celeb-birthday" />
                </TabsContent>
                <TabsContent value="wedding">
                  <MessageEditor
                    subjectKey="wedding_subject" subjectValue={settings.wedding_subject}
                    messageKey="wedding_message" messageValue={settings.wedding_message}
                    up={up} testidPrefix="celeb-wedding" />
                </TabsContent>
                <TabsContent value="work">
                  <MessageEditor
                    subjectKey="work_anniversary_subject" subjectValue={settings.work_anniversary_subject}
                    messageKey="work_anniversary_message" messageValue={settings.work_anniversary_message}
                    up={up} testidPrefix="celeb-work" />
                </TabsContent>
              </Tabs>
            </div>

            <div className="lg:col-span-2">
              <Label className="flex items-center gap-1.5"><Mail size={12} /> Live Preview</Label>
              <div className="mt-2 border border-slate-200 rounded-xl overflow-hidden bg-slate-50" style={{ minHeight: 420 }} data-testid="celeb-preview-frame">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-full text-xs text-slate-400 py-24">Rendering…</div>
                ) : previewHtml ? (
                  <iframe title="Card Preview" srcDoc={previewHtml} className="w-full" style={{ height: 500, border: 0 }} />
                ) : (
                  <div className="flex flex-col items-center justify-center text-xs text-slate-400 py-24 px-4 text-center">
                    Click <strong className="mx-1">Refresh Preview</strong> to see your card.
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ChannelToggle = ({ label, icon: Icon, on, setOn, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-slate-500" />
      <p className="text-sm font-medium text-slate-700">{label}</p>
    </div>
    <Switch checked={!!on} onCheckedChange={setOn} data-testid={testid} />
  </div>
);

const CelebrantGroup = ({ icon: Icon, color, label, items }) => {
  if (!items?.length) return null;
  const colorMap = {
    pink: 'bg-pink-100 text-pink-700 border-pink-200',
    rose: 'bg-rose-100 text-rose-700 border-rose-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
  };
  return (
    <div>
      <div className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${colorMap[color]}`}>
        <Icon size={10} /> {label}
      </div>
      <ul className="mt-1.5 space-y-0.5">
        {items.map((it, i) => <li key={i} className="text-xs text-slate-700">• {it}</li>)}
      </ul>
    </div>
  );
};

const MessageEditor = ({ subjectKey, subjectValue, messageKey, messageValue, up, testidPrefix }) => (
  <div className="space-y-3 mt-3">
    <div>
      <Label>Email Subject</Label>
      <Input value={subjectValue || ''} onChange={e => up(subjectKey, e.target.value)} data-testid={`${testidPrefix}-subject`} />
    </div>
    <div>
      <Label>Message Body</Label>
      <Textarea rows={5} value={messageValue || ''} onChange={e => up(messageKey, e.target.value)} data-testid={`${testidPrefix}-message`} />
      <p className="text-[10px] text-slate-400 mt-1">Available placeholders: <span className="font-mono">{PLACEHOLDER_HINTS}</span></p>
    </div>
  </div>
);

export default CelebrationSettingsPage;
